/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faBracketsCurly, faCog } from "@fortawesome/pro-regular-svg-icons";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Container } from "reactstrap";
import ED from "../../../img/logo-ed.png";
import useAvosApi from "../../../utils/useAvosApiHook";
import { useDebugTargets } from "../../../utils/useDebugTargets";
import { FromLink } from "../../../utils/useNavigateFrom";
import { Button } from "../../Helper/Button";
import DebugModal from "../../Helper/DebugModal";
import { isClientPortal, isSupplierPortal } from "../../utils";

export default function Footer() {
    const user = useSelector((state: any) => state.auth.user);
    const isAdmin = user.is_admin;

    const isAdminBlockStyle = css`
        ${isAdmin ? "bottom: 3.8rem;" : "bottom: 0;"}
    `;
    const footerBlockHeight = css`
        ${isAdmin ? "height: 15rem;" : "height: 12.5rem;"}
    `;

    return (
        <Container className="w-100 pt-5">
            <div css={footerBlockHeight} className={`footer position-relative text-center text-muted d-flex align-items-center border-top ${isAdmin ? "pb-5" : "mb-5"}`}>
                <div css={isAdminBlockStyle} className="footer-copyright d-flex flex-column align-items-center gap-1">
                    <div>
                        <small>
                            Powered by
                            <img src={ED} alt="Logo Experience Data" className="ms-2 d-inline-block" />
                            <a className="text-dark ms-1 text-decoration-none" target="_blank" href="https://www.experiencedata.nl/" rel="noreferrer">Experience Data</a>
                        </small>
                    </div>

                    <div>
                        <VersionTag />
                    </div>
                </div>
            </div>

            {isAdmin && !isSupplierPortal() && !isClientPortal() && (
                <AdminBanner />
            )}
        </Container>
    );
}

function VersionTag() {
    const user = useSelector((state: any) => state.auth.user);
    const frontend_version = process.env?.REACT_APP_VERSION;
    const timer = useRef<NodeJS.Timeout | null>(null);

    const isAdmin = user.is_admin;
    const [getBackendVersion, backendVersion] = useAvosApi("/health/version");

    const backend_version = backendVersion?.version;
    const version_not_equal = frontend_version && backend_version && frontend_version !== backend_version;

    useEffect(() => {
        // * Check backend version every 10 seconds, but do not spam with toast if temporarily unavailable/no interwebs
        getBackendVersion(null, { no_toast: true });

        timer.current = setInterval(() => {
            getBackendVersion(null, { no_toast: true });
        }, 10000);

        return () => {
            if (timer.current) {
                clearInterval(timer.current);
            }
        };
    }, []);

    if (!frontend_version) {
        return <small>Version <b>dev</b></small>;
    }

    if (isAdmin) {
        return (
            <small title={`frontend: ${frontend_version} - backend: ${backend_version}`}>
            Version <b><a href={`https://github.com/experiencedata/avos_server/commit/${frontend_version}`} target="_blank" rel="noreferrer">{frontend_version.slice(0, 7)}</a></b>
                {version_not_equal && <span> / <a className="fw-bold" href={`https://github.com/experiencedata/avos_server/commit/${backend_version}`} target="_blank" rel="noreferrer">{backend_version.slice(0, 7)}</a></span>}
            </small>
        );
    }

    return (
        <small title={`frontend: ${frontend_version} / backend: ${backend_version}`}>
            Version <b>{frontend_version.slice(0, 7)}</b>{version_not_equal && <span className="fw-bold">-{backend_version.slice(0, 7)}</span>}
        </small>
    );
}

function AdminBanner() {
    const params = useParams();
    const user = useSelector((state: any) => state.auth.user);
    const registeredTargets = useDebugTargets();

    const [debugModalState, setDebugModal] = useState({});
    const navigate = useNavigate();
    const tenant_id = user.tenant?.tenant_id;
    const tenant_name = user.tenant?.tenant_name;

    const restoreTenantAndNavigate = () => {
        navigate("/tenants");
    };

    const urls = [
        {
            url: `/tenants/${tenant_id}`,
            text: `Edit ${tenant_name} config`
        },
        ...registeredTargets,
    ] as any;

    // * Extra padding is added to make space for the sticky navbar on the ripeningroom
    return (
        <>
            {urls
                .filter((i) => i.debug)
                .map((i) => (
                    <DebugModal
                        key={i.debug.title}
                        docProps={i.debug.props}
                        title={i.debug.title}
                        isOpen={debugModalState[i.debug.title]}
                        close={() => setDebugModal((prev) => ({ ...prev, [i.debug.title]: false }))}
                    />
                ))}

            <Alert color="info" css={params.location_id ? css`padding-bottom: 8rem;` : ""}>
                <div>
                    <div className="text-center font-weight-bold pb-4">
                        You are impersonating the frontend as <b>{tenant_name}</b>.
                    </div>

                    <div className="text-center d-flex flex-wrap justify-content-center gap-2">
                        {urls
                            .filter((i) => !i.debug)
                            .map((i, index) => (
                                <FromLink
                                    key={index}
                                    className="btn-sm btn btn-secondary"
                                    to={i.url}
                                >
                                    <FontAwesomeIcon icon={faCog} />{" "}
                                    {i.text}
                                </FromLink>
                            ))}

                        {urls
                            .filter((i) => i.debug)
                            .map((i) => (
                                <Button
                                    key={i.debug.title}
                                    color={"secondary"}
                                    size="sm"
                                    icon={faBracketsCurly}
                                    onClick={() => setDebugModal((prev) => ({ ...prev, [i.debug.title]: true }))}
                                >
                                    {i.debug.title}
                                </Button>
                            ))}

                        <Button color="secondary" size="sm" onClick={restoreTenantAndNavigate} icon={faArrowLeft}>
                            Back to tenants
                        </Button>
                    </div>
                </div>
            </Alert>
        </>
    );
}
